import { cn } from "@simpro/ui";
import { Fragment, type ReactNode } from "react";

export interface SectionHeadingProps {
  /** The section heading's id, for the section's `aria-labelledby`. */
  id: string;
  /** The small line above the title ("Ritual of the season"). */
  eyebrow?: string | null;
  /** A plain title has its last word set in italic in the accent; a node is drawn as given. */
  title: ReactNode;
  /** One or two sentences under the title. */
  lead?: string | null;
  /** A link or button that belongs to the whole section ("View all"), placed opposite the title. */
  action?: ReactNode;
  /** Centred over the section, for galleries and testimonials. */
  centred?: boolean;
  /** The heading's level; a page's first section title is an `h1`. */
  level?: 1 | 2;
  /** Light text, for a heading on a green band. */
  onInk?: boolean;
  className?: string;
}

/**
 * The one way a section introduces itself (design note salon-booking-organic-wellness.md section 6):
 * an optional sage rule and line, a title in the display face on the board's large scale with its last
 * word in italic in the accent ("Kind *words*", "Come find *us*"), an optional lead, and an optional
 * action on the right from a tablet up.
 */
export function SectionHeading({
  id,
  eyebrow,
  title,
  lead,
  action,
  centred = false,
  level = 2,
  onInk = false,
  className,
}: SectionHeadingProps) {
  const Heading = level === 1 ? "h1" : "h2";
  return (
    <div
      className={cn(
        "flex flex-col gap-6",
        centred ? "items-center text-center" : "md:flex-row md:items-end md:justify-between md:gap-10",
        className,
      )}
    >
      <div className={cn("flex max-w-[40rem] flex-col gap-4", centred && "items-center")}>
        {eyebrow ? (
          <p className={cn("flex items-center gap-3 text-sm font-medium", onInk ? "text-on-bar-muted" : "text-subtle")}>
            <span aria-hidden="true" className="h-px w-10 bg-sage" />
            {eyebrow}
          </p>
        ) : null}
        <Heading id={id} className={cn("display-section", onInk ? "text-on-ink" : "text-heading")}>
          {typeof title === "string" ? <LastWordLit text={title} onInk={onInk} /> : title}
        </Heading>
        {lead ? (
          <p
            className={cn(
              "max-w-xl text-[15px] leading-[1.7] md:text-[17px]",
              onInk ? "text-on-ink/80" : "text-content",
            )}
          >
            {lead}
          </p>
        ) : null}
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
  );
}

/**
 * A title with its last word in italic in the accent, as every heading on the board has. The space is a
 * sibling of the words, so the heading's accessible name reads as one line.
 */
function LastWordLit({ text, onInk }: { text: string; onInk: boolean }) {
  const trimmed = text.trim();
  const cut = trimmed.lastIndexOf(" ");
  if (cut < 0) return <>{trimmed}</>;
  return (
    <Fragment>
      {trimmed.slice(0, cut)}{" "}
      <em className={onInk ? "text-ink-link" : "text-highlight"}>{trimmed.slice(cut + 1)}</em>
    </Fragment>
  );
}
